package com.arena_web.ui.components.chat

import androidx.compose.runtime.*
import com.arena_web.data.dto.FollowUserDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.widgets.InlineSpinner
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.bodyLMedium
import com.arena_web.ui.theme.bodyLRegular
import com.arena_web.ui.theme.bodyMRegular
import com.arena_web.ui.theme.bodyXlRegular
import com.arena_web.ui.theme.h3
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.TextInput

private val FieldBackground =
    "${AppGradients.InteractiveSecondary} padding-box, " +
        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
        "${AppGradients.ButtonBorderGradientPrimary} border-box"

/**
 * Shared "Invite members" picker — search + multi-select friend list + an
 * "Invite (N)" action. Selection is held internally; [onInvite] receives the
 * chosen user ids. Renders inside both the desktop popup and the mobile sheet
 * (see [EditChatContent] for the `--scale` rationale).
 */
@Composable
fun InviteMembersContent(
    searchQuery: String,
    onSearchChanged: (String) -> Unit,
    results: List<FollowUserDto>,
    isLoading: Boolean,
    isLoadingMore: Boolean,
    hasMore: Boolean,
    isInviting: Boolean,
    onLoadMore: () -> Unit,
    onInvite: (List<String>) -> Unit,
    onCancel: () -> Unit,
) {
    val t = LocalAppStrings.current
    val selected = remember { mutableStateListOf<String>() }

    Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(24)) }) {
        SpanText(t.chatInviteMembersTitle, modifier = Modifier.h3().color(com.arena_web.ui.theme.ThemeVars.Text2))

        // Search field.
        Row(
            modifier = Modifier.fillMaxWidth().styleModifier {
                property("height", s(44))
                property("border-radius", s(28))
                property("padding", "0 ${s(8)} 0 ${s(12)}")
                property("gap", s(6))
                property("border", "0.5px solid transparent")
                property("background", FieldBackground)
                property("box-sizing", "border-box")
            },
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier.sSize(24).styleModifier {
                    property("flex-shrink", "0")
                    property("background-color", "#818D9C")
                    property("mask", "url('${AppIcons.Search}') no-repeat center / contain")
                    property("-webkit-mask", "url('${AppIcons.Search}') no-repeat center / contain")
                }
            )
            TextInput(
                value = searchQuery,
                attrs = Modifier
                    .fillMaxWidth()
                    .bodyLRegular()
                    .color(com.arena_web.ui.theme.ThemeVars.Text1)
                    .styleModifier {
                        property("background", "transparent")
                        property("border", "none")
                        property("outline", "none")
                        property("min-width", "0")
                        property("font-family", "Urbanist, sans-serif")
                    }
                    .toAttrs {
                        attr("placeholder", t.chatSearchFriendsPlaceholder)
                        onInput { e -> onSearchChanged(e.target.asDynamic().value as String) }
                    }
            )
        }

        // Friend list — multi-select.
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("max-height", "52vh")
                    property("overflow-y", "auto")
                    property("scrollbar-width", "none")
                    property("gap", s(20))
                }
                .attrsModifier {
                    onScroll { evt ->
                        if (hasMore && !isLoading && !isLoadingMore) {
                            val el = evt.target.unsafeCast<org.w3c.dom.HTMLElement>()
                            if (el.scrollHeight - el.scrollTop - el.clientHeight < 80.0) onLoadMore()
                        }
                    }
                }
        ) {
            if (isLoading) {
                Box(modifier = Modifier.fillMaxWidth().sPadding(24, 0), contentAlignment = Alignment.Center) {
                    Box {} /* no loading indicator (user pref) */
                }
            } else if (results.isEmpty()) {
                Box(modifier = Modifier.fillMaxWidth().sPadding(24, 0), contentAlignment = Alignment.Center) {
                    SpanText(
                        if (searchQuery.isBlank()) t.chatNoFriendsToInvite else t.chatNoResults,
                        modifier = Modifier.bodyMRegular().color(com.arena_web.ui.theme.ThemeVars.Text3)
                    )
                }
            } else {
                results.forEach { user ->
                    InviteRow(
                        user = user,
                        isSelected = user.userId in selected,
                        onToggle = {
                            if (user.userId in selected) selected.remove(user.userId)
                            else selected.add(user.userId)
                        }
                    )
                }
                if (isLoadingMore) {
                    Box(modifier = Modifier.fillMaxWidth().sPadding(8, 0), contentAlignment = Alignment.Center) {
                        Box {} /* no loading indicator (user pref) */
                    }
                }
            }
        }

        // Action buttons.
        Row(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(12)) }) {
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("flex", "1")
                        property("height", s(44))
                        property("border-radius", s(100))
                        property("border", "0.3px solid transparent")
                        property("background", FieldBackground)
                        property("box-sizing", "border-box")
                    }
                    .cursor(Cursor.Pointer)
                    .onClick { onCancel() },
                contentAlignment = Alignment.Center
            ) {
                SpanText(t.commonCancel, modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2))
            }

            val canInvite = selected.isNotEmpty() && !isInviting
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("flex", "1")
                        property("height", s(44))
                        property("border-radius", s(100))
                        property("background", "#0061ff")
                        property("opacity", if (selected.isNotEmpty()) "1" else "0.5")
                    }
                    .cursor(if (canInvite) Cursor.Pointer else Cursor.Default)
                    .onClick { if (canInvite) onInvite(selected.toList()) },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    when {
                        isInviting -> t.chatInviting
                        selected.isEmpty() -> t.chatInvite
                        else -> t.chatInviteCount(selected.size)
                    },
                    modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite)
                )
            }
        }
    }
}

@Composable
private fun InviteRow(user: FollowUserDto, isSelected: Boolean, onToggle: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .cursor(Cursor.Pointer)
            .onClick { onToggle() }
            .styleModifier { property("gap", s(8)) },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Avatar — mini player card if available, else a rounded tile.
        val cardFrameUrl = user.cardImageUrl
        if (cardFrameUrl != null) {
            Box(
                modifier = Modifier.styleModifier {
                    property("flex-shrink", "0")
                    property("width", s(40))
                    property("height", s(60))
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                },
                contentAlignment = Alignment.Center
            ) {
                PlayerCard(
                    playerName = "",
                    rating = 0,
                    position = "",
                    sport = "football",
                    size = PlayerCardSize.Highlight,
                    cardFrameUrl = cardFrameUrl,
                    avatarUrl = user.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                    hideRating = true,
                    hideShadow = true,
                )
            }
        } else {
            val avatarUrl = user.avatarUrl
            Box(
                modifier = Modifier
                    .sWidth(40)
                    .sHeight(60)
                    .styleModifier {
                        property("border-radius", s(6))
                        property("background", "rgba(60, 121, 241, 0.15)")
                        property("overflow", "hidden")
                        property("flex-shrink", "0")
                    },
                contentAlignment = Alignment.Center
            ) {
                if (avatarUrl != null) {
                    Image(src = avatarUrl, modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover))
                } else {
                    SpanText(
                        user.username.take(1).uppercase(),
                        modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }
            }
        }

        Column(
            modifier = Modifier.weight(1f).styleModifier {
                property("min-width", "0")
                property("gap", s(2))
            }
        ) {
            SpanText(
                user.fullName ?: user.username,
                modifier = Modifier
                    .bodyXlRegular()
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .whiteSpace(WhiteSpace.NoWrap)
                    .overflow(Overflow.Hidden)
                    .textOverflow(TextOverflow.Ellipsis)
            )
            SpanText("@${user.username}", modifier = Modifier.bodyMRegular().color(com.arena_web.ui.theme.ThemeVars.Text3))
        }

        // Selection indicator — filled blue check when selected, else outline.
        Box(
            modifier = Modifier
                .sSize(24)
                .sBorderRadius(100)
                .styleModifier {
                    property("flex-shrink", "0")
                    if (isSelected) {
                        property("background", "#0061ff")
                        property("border", "none")
                    } else {
                        property("background", "transparent")
                        property("border", "1.5px solid rgba(255,255,255,0.4)")
                    }
                },
            contentAlignment = Alignment.Center
        ) {
            if (isSelected) {
                Box(
                    modifier = Modifier.sSize(12).styleModifier {
                        property("background-color", "#FFFFFF")
                        property("mask", "url('${AppIcons.Check}') no-repeat center / contain")
                        property("-webkit-mask", "url('${AppIcons.Check}') no-repeat center / contain")
                    }
                )
            }
        }
    }
}
